import { useEffect, useImperativeHandle, useState, useContext } from "react";
import { Paper, Box } from "@material-ui/core";
import FiltersTable from "../../../components/FiltersTable";
import { useLocation } from "react-router-dom";
import { getSoftScheduleListApi } from "./Service";
import { exportExcelData, transformKeysToTitleCase, convertToInt } from "../../common";
import { commonStrings, softScheduleStrings } from "../../../components/StringBundle";
import { useGlobalStyles } from "../../../components/globalStyles";
import { GetSoftScheduleGridColumns } from "./constant";
import { generateFilterPayload, transformData } from '../../../components/FilterUtils';
import Snackbar from "../../../components/Snackbar";
import useToken from "../../../hooks/useToken";
import { AuthContext } from "../../../context/auth-context";

const SoftSchedulingList = ({
  loadingExcelVal,
  exportRef,
  activepage,
  setActivepage,
  totalrecord,
  updateTotalRecords,
  period,
  messageEmpty,
  company,
}) => {
  const location = useLocation();
  const softScheduleString = softScheduleStrings?.forms;
  const globalClasses = useGlobalStyles();
  const { getCurrentUserDetails } = useToken();
  const currentUserRole = getCurrentUserDetails().role;

  const savedPageSize = sessionStorage.getItem("selectedpagesize");
  const initialPageSize = savedPageSize ? parseInt(savedPageSize) : 10;
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  
  const savedFilterRaw = sessionStorage.getItem(`FiltersTable.${location.pathname}`);
  const initialFilter = savedFilterRaw ? JSON.parse(savedFilterRaw) : [];
  const [searchValue, setSearchValue] = useState(initialFilter);

  const [hasError, setHasError] = useState(false);
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSnack, setSnack] = useState({});
  const columns = GetSoftScheduleGridColumns(currentUserRole);

  // Export method to parent
  useImperativeHandle(exportRef, () => ({
    exportExcel,
  }));

  useEffect(() => {
    if(period) {
      callListApi(activepage, rowsPerPage, searchValue);
    }
  }, [period, activepage, rowsPerPage, company]); 

  // Payload formation
  function getSearchPayload(page, pagelimit, searchtext) {
    return transformData(
      generateFilterPayload(searchtext, {
        period: period,
        page: parseInt(sessionStorage.getItem('selectedpagenumber')) || page,
        limit: pagelimit,
        company_name: company,
      })
    );
  }

  // List API call
  async function callListApi(
    page = activepage,
    pagelimit = rowsPerPage,
    searchtext = searchValue
  ) {
    setAllData(null);
    try {
      const searchPayload = getSearchPayload(page, pagelimit, searchtext);

      const response = await getSoftScheduleListApi(searchPayload);
      if (response?.order_soft_schedule_grid) {
        const dataWithKeys = response.order_soft_schedule_grid.map(item => ({
          ...item,
          key: item.record_id
        }));
        setAllData(dataWithKeys);
        updateTotalRecords(response.total_count);
      } else {
        setAllData([]);
        updateTotalRecords(0);
      }
    } catch (e) {
      setHasError(true);
      setAllData([]);
      updateTotalRecords(0);
      setSnack({
        open: true,
        message: e?.response?.data || commonStrings.serverError,
        severity: commonStrings.error,
      });
    } 
  }

  // Export Excel API call
  async function callListApiExportExcel(
    page = activepage,
    pagelimit = rowsPerPage,
    searchtext = searchValue
  ) {
    try {
      setLoading(true);
      loadingExcelVal(true);
      const searchPayload = getSearchPayload(page, pagelimit, searchtext);
      const response = await getSoftScheduleListApi(searchPayload);
      if (response?.order_soft_schedule_grid) {
        const transformedData = response?.order_soft_schedule_grid.map(item => transformKeysToTitleCase(item));
        exportExcelData(transformedData, softScheduleString.softSchedule);
      }
    } catch (e) {
      setSnack({
        open: true,
        message: e?.response?.data || commonStrings.serverError,
        severity: commonStrings.error,
      });
    } finally {
      setLoading(false);
      loadingExcelVal(false);
    }
  }

  // Pagination change
  async function paginationChange(page, pagesize) {
    setActivepage(page);
    setRowsPerPage(pagesize);
    sessionStorage.setItem('selectedpagesize', pagesize);
    sessionStorage.setItem('selectedpagenumber', page);  
  }

  // Filter Data Column (Internal Table Search)
  async function filterDataColumn(searchArr) {
    const val = searchArr === "" ? [] : searchArr;
    setSearchValue(val);
    setActivepage(1);
    sessionStorage.setItem('selectedpagenumber', 1);
    callListApi(1, rowsPerPage, val);
  }

  // Export Excel Button Click
  function exportExcel() {
    callListApiExportExcel(1, totalrecord, searchValue);
  }

  return (
    <Paper className={globalClasses.qliroot}>
      {showSnack.open && (<Snackbar {...showSnack} handleClose={() => setSnack({ open: false, message: "", severity: "" })} />)}
      {loading && (
        <Box className="overlay_css">
          <p>{commonStrings.dataLoading}</p>
        </Box>
      )}
      <Box className={globalClasses.positionRelative}>
        <FiltersTable
          columns={columns}
          tableData={allData}
          onPaginationChange={paginationChange}
          totalrecord={totalrecord}
          filterDataColumn={filterDataColumn}
          apiFilter={true}
          hasError={hasError}
          pagination={activepage}
          messageEmpty={messageEmpty}
        />
      </Box>
    </Paper>
  );
}

export default SoftSchedulingList;